import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/**
 * Mobile only: extra px, on top of the fixed header's height, at which the sticky bar shows.
 * With 0 the bar shows as soon as the top edge of the original buy buttons slides under the
 * header, even by a pixel. Positive = earlier (while still clear of the header), negative = later.
 */
const MOBILE_EARLY_TRIGGER_OFFSET_PX = 0;

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {MutationObserver | null} */
  #pendingStateObserver = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #footerInView = false;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /**
   * Hiding the bar when the footer comes into view is desktop-only; on mobile the bar stays
   * put all the way to the bottom of the page.
   */
  #desktopQuery = window.matchMedia('(min-width: 750px)');

  /** @type {number} */
  #appliedTopInset = 0;

  /** @type {MutationObserver | null} */
  #headerHeightObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    // header.js writes --header-height to <body> after its own async measurement and on every
    // resize, so re-arm the trigger whenever the inset it feeds changes.
    this.#headerHeightObserver = new MutationObserver(() => {
      if (this.#getTopInset() !== this.#appliedTopInset) this.#setupIntersectionObserver();
    });
    this.#headerHeightObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#desktopQuery.addEventListener('change', this.#syncFooterState, { signal });
    this.#desktopQuery.addEventListener('change', () => this.#setupIntersectionObserver(), { signal });

    this.#getInitialQuantity();
    this.#observePendingState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#headerHeightObserver?.disconnect();
    this.#pendingStateObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    const topInset = this.#getTopInset();
    const isMobile = !this.#desktopQuery.matches;
    this.#appliedTopInset = topInset;
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (isMobile) {
          // Show as soon as the top edge of the buy buttons passes under the header.
          // The [0, 1] thresholds make the observer fire when the block stops being fully visible.
          const above = entry.target.getBoundingClientRect().top < topInset;
          if (above && !this.#isStuck) {
            this.#showStickyBar();
          } else if (!above && this.#isStuck) {
            this.#hiddenByBottom = false;
            this.#hideStickyBar();
          }
          return;
        }

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            // Element is above viewport (or, on mobile, under the header) - show sticky bar
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      },
      { rootMargin: `-${topInset}px 0px 0px 0px`, threshold: isMobile ? [0, 1] : 0 }
    );

    // Observer for footer visibility - hides sticky bar at page bottom (desktop only)
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#footerInView = entry.isIntersecting;
        this.#syncFooterState();
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsBlock = /** @type {HTMLElement} */ (buyButtonsBlock);
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Px trimmed off the top of the viewport when deciding the buy buttons are out of view.
   * Mobile only: fixed header height (--header-height) plus MOBILE_EARLY_TRIGGER_OFFSET_PX.
   * Desktop stays 0.
   */
  #getTopInset() {
    if (this.#desktopQuery.matches) return 0;
    const header = parseFloat(document.body.style.getPropertyValue('--header-height')) || 0;
    return Math.round(header + MOBILE_EARLY_TRIGGER_OFFSET_PX);
  }

  /**
   * Hides the bar while the footer is in view on desktop, and brings it back once the footer
   * leaves view or the viewport drops to mobile (where the footer never hides it).
   */
  #syncFooterState = () => {
    const hideForFooter = this.#footerInView && this.#desktopQuery.matches;

    if (hideForFooter && this.#isStuck) {
      this.#hiddenByBottom = true;
      this.#hideStickyBar();
    } else if (!hideForFooter && this.#hiddenByBottom) {
      const rect = this.#buyButtonsBlock?.getBoundingClientRect();
      // Only show if buy buttons are above the viewport (scrolled past)
      if (rect && (rect.bottom < 0 || rect.top < this.#getTopInset())) {
        this.#hiddenByBottom = false;
        this.#showStickyBar();
      }
    }
  };

  /**
   * Mirrors the main product form's add-to-cart pending state onto the sticky bar's own
   * button.
   *
   * The sticky button is a puppet: it clicks the real button inside the form, so the form
   * owns the request and puts *that* button into its pending state. Rather than run a
   * second, separately-timed state here - which could get stuck if the request failed in a
   * way the sticky bar doesn't hear about - the bar just reflects whatever the form is
   * already showing, so the two can't disagree and can't outlive each other.
   */
  #observePendingState() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#pendingStateObserver = new MutationObserver(() => this.#syncPendingState());
    this.#pendingStateObserver.observe(productForm, {
      attributes: true,
      attributeFilter: ['aria-busy'],
      subtree: true,
    });
  }

  #syncPendingState() {
    const button = this.refs.addToCartButton;
    if (!button) return;

    const pending = Boolean(
      this.#getProductForm()?.querySelector('[data-add-to-cart-trigger][aria-busy="true"]')
    );
    const alreadyPending = button.getAttribute('aria-busy') === 'true';

    if (pending === alreadyPending) return;

    if (pending) {
      button.dataset.wasDisabled = String(button.disabled === true);
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      if (button.dataset.wasDisabled !== 'true') button.disabled = false;
      delete button.dataset.wasDisabled;
    }
  }

  // Public action handlers
  /**
   * Scrolls the main variant picker into view
   */
  handleEditClick = () => {
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) return;
    scrollIntoView(variantPicker, { block: 'center' });
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.#getProductForm()?.dataset.openCartDrawerOnAdd === 'true') return;

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd(this.refs.addToCartButton);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
